(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document) {
    root.DentisPreorder = api;
    api.install(root);
  }
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';

  const normal = () => ({ preorder: false, text: null, nextDelivery: null });
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

  function futureDate(value, now) {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(0);
    date.setFullYear(year, month - 1, day);
    date.setHours(12, 0, 0, 0);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
    const today = new Date(now);
    if (!Number.isFinite(today.getTime())) return null;
    today.setHours(23, 59, 59, 999);
    return date > today ? value : null;
  }

  function expectation(date, strings, locale) {
    if (!date) return strings.generic;
    const [year, month, day] = date.split('-').map(Number);
    const localDate = new Date(0);
    localDate.setFullYear(year, month - 1, day);
    localDate.setHours(12, 0, 0, 0);
    const formatted = localDate.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
    return strings.dated.replace('[date]', formatted);
  }

  /**
   * Resolve display copy only. SKU keys and marketer messages are never normalized.
   * @param {string} sku
   * @param {object} snapshot
   * @param {{now?: Date, strings: object, locale?: string}} options
   * @returns {{preorder: boolean, text: string|null, nextDelivery: string|null}}
   */
  function resolve(sku, snapshot, { now = new Date(), strings, locale } = {}) {
    if (typeof sku !== 'string' || !sku || !snapshot || !snapshot.skus || !own(snapshot.skus, sku)) return normal();
    const entry = snapshot.skus[sku];
    if (!entry || entry.status !== 'not_in_stock') return normal();
    const nextDelivery = futureDate(entry.nextDelivery, now);
    const message = typeof entry.message === 'string' && entry.message.trim() ? entry.message : null;
    return { preorder: true, text: message || expectation(nextDelivery, strings, locale), nextDelivery };
  }

  function aggregate(lines, snapshot, options) {
    const affected = [];
    const seen = new Set();
    for (const line of lines) {
      if (!line || seen.has(line.sku)) continue;
      seen.add(line.sku);
      const state = resolve(line.sku, snapshot, options);
      if (state.preorder) affected.push(state);
    }
    if (!affected.length) return normal();
    const nextDelivery = affected.every(state => state.nextDelivery)
      ? affected.map(state => state.nextDelivery).sort().pop()
      : null;
    return { preorder: true, text: expectation(nextDelivery, options.strings, options.locale), nextDelivery };
  }

  function readJSON(node) {
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  function setText(node, text) {
    if (node && node.textContent !== text) node.textContent = text;
  }

  function showNotice(node, text) {
    setText(node, text || '');
    node.classList.toggle('hidden', !text);
  }

  function install(win, { clock = () => new Date() } = {}) {
    const doc = win.document;
    const config = win.DentisPreorderConfig;
    if (!config || ['cta', 'generic', 'dated', 'shippingHeading', 'soldOutNotice'].some(key =>
      typeof config[key] !== 'string' || !config[key].trim())) return;
    let snapshot;
    const products = new WeakMap();
    const visibility = new WeakMap();
    const options = () => ({ now: clock(), strings: config, locale: config.locale });

    function toggleNormal(node, active) {
      if (active) {
        if (!visibility.has(node)) visibility.set(node, node.classList.contains('hidden'));
        node.classList.add('hidden');
      } else if (visibility.has(node)) {
        node.classList.toggle('hidden', visibility.get(node));
        visibility.delete(node);
      }
    }

    // The native picker writes the same strings; reading them at paint time keeps both writers in agreement.
    function nativeString(key) {
      const value = win.variantStrings && win.variantStrings[key];
      return typeof value === 'string' && value.trim() ? value : null;
    }

    function productState(metadata) {
      if (products.has(metadata)) return products.get(metadata);
      const data = readJSON(metadata);
      if (!data || !Array.isArray(data.variants)) return null;
      const section = metadata.closest('[data-dentis-preorder-section]');
      const form = doc.getElementById(data.formId);
      if (!section || !form || !section.contains(form)) return null;
      const state = {
        section, form, variants: new Map(data.variants.map(variant => [String(variant.id), variant])),
        invalid: false, label: form.querySelector('[data-dentis-preorder-label]'), normalLabel: null
      };
      if (state.label) {
        // A sold-out first paint renders the sold-out label, which is not the label to restore.
        const initial = state.variants.get(String(data.initialVariantId));
        state.normalLabel = initial && !initial.available
          ? nativeString('addToCart') || state.label.textContent
          : state.label.textContent;
      }
      products.set(metadata, state);
      return state;
    }

    function paintSlots(root, text) {
      for (const node of root.querySelectorAll('[data-dentis-preorder-normal]')) toggleNormal(node, Boolean(text));
      for (const node of root.querySelectorAll('[data-dentis-preorder-message]')) showNotice(node, text);
      for (const node of root.querySelectorAll('[data-dentis-preorder-heading]')) {
        setText(node, config.shippingHeading);
        node.classList.toggle('hidden', !text);
      }
    }

    function paintDelivery(section, text, soldOut) {
      // A sold-out variant promises no delivery at all; shipping accordions say why instead.
      for (const delivery of section.querySelectorAll('[data-dentis-preorder-delivery]')) {
        delivery.classList.toggle('hidden', soldOut);
        paintSlots(delivery, soldOut ? null : text);
      }
      for (const shipping of section.querySelectorAll('[data-dentis-preorder-shipping]')) {
        paintSlots(shipping, soldOut ? config.soldOutNotice : text);
      }
    }

    function paintProduct(product) {
      const input = product.form.querySelector('input[name="id"]');
      // The submitted ID is authoritative, including unknown Eevy virtual parents.
      const variant = !product.invalid && input ? product.variants.get(String(input.value)) : null;
      const soldOut = Boolean(variant) && !variant.available;
      const state = variant && !soldOut ? resolve(variant.sku, snapshot, options()) : normal();
      if (product.label) {
        const soldOutLabel = nativeString('soldOut');
        const shown = product.label.textContent;
        if (soldOut) {
          if (soldOutLabel) setText(product.label, soldOutLabel);
        } else if (state.preorder) {
          setText(product.label, config.cta);
        } else if (shown === config.cta || (variant && soldOutLabel && shown.trim() === soldOutLabel.trim())) {
          setText(product.label, product.normalLabel);
        }
      }
      for (const note of product.section.querySelectorAll('[data-dentis-preorder-note]')) showNotice(note, state.text);
      for (const note of product.section.querySelectorAll('[data-dentis-sold-out-note]')) note.classList.toggle('hidden', !soldOut);
      paintDelivery(product.section, state.text, soldOut);
    }

    function eachProduct(callback) {
      for (const metadata of doc.querySelectorAll('[data-dentis-preorder-product]')) {
        const product = productState(metadata);
        if (product) callback(product);
      }
    }

    function paintCart() {
      for (const drawer of doc.querySelectorAll('#CartDrawer .drawer__inner')) {
        const metadata = drawer.querySelector('[data-dentis-preorder-cart]');
        const lines = metadata && readJSON(metadata);
        const actual = Array.isArray(lines) ? lines : [];
        const variants = new Map();
        const skus = new Set();
        for (const line of actual) {
          if (!line || typeof line.sku !== 'string' || !line.sku) continue;
          skus.add(line.sku);
          variants.set(String(line.id), line.sku);
        }
        for (const note of drawer.querySelectorAll('[data-dentis-preorder-cart-note]')) {
          const sku = note.hasAttribute('data-dentis-preorder-sku')
            ? note.getAttribute('data-dentis-preorder-sku')
            : variants.get(note.getAttribute('data-dentis-preorder-variant'));
          showNotice(note, skus.has(sku) ? resolve(sku, snapshot, options()).text : null);
        }
        const summary = aggregate(actual, snapshot, options());
        for (const estimate of drawer.querySelectorAll('[data-dentis-preorder-cart-normal]')) toggleNormal(estimate, summary.preorder);
        for (const notice of drawer.querySelectorAll('[data-dentis-preorder-cart-summary]')) showNotice(notice, summary.text);
      }
    }

    function refresh() {
      eachProduct(paintProduct);
      paintCart();
    }

    function onSelection(event) {
      eachProduct(product => {
        const target = event.target;
        if (event.type === 'change') {
          if (!target.matches || !target.matches('input[name="id"]') || target.form !== product.form) return;
          product.invalid = false;
        } else if (product.section.contains(target)) {
          product.invalid = !(event.detail && (event.detail.variantId || event.detail.variant));
        }
        // Document-level picker events carry no form owner. Re-read each owned input.
        paintProduct(product);
      });
    }

    function nativeLabel(form, variant, written) {
      eachProduct(product => {
        // input[name=id] shadows form.id in real browsers.
        if (product.form.getAttribute('id') !== form.getAttribute('id')) return;
        product.invalid = !variant;
        if (written && product.label && (!variant || variant.available)) product.normalLabel = product.label.textContent;
        paintProduct(product);
      });
    }

    win.addEventListener('dentis:stock-status:ready', event => {
      snapshot = event.detail;
      refresh();
    });
    doc.addEventListener('change', onSelection);
    doc.addEventListener('variantChanged', onSelection);
    doc.addEventListener('variant:changed', onSelection);
    doc.addEventListener('dentis:cart-drawer:updated', paintCart);
    doc.addEventListener('shopify:section:load', refresh);
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', refresh, { once: true });
    snapshot = win.DentisStockStatus;
    refresh();
    win.console.debug('[DentisPreorder] init');
    api.nativeLabel = nativeLabel;
    return { refresh, nativeLabel };
  }

  const api = { resolve, aggregate, install };
  return api;
});
